import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';
import { useVendor } from '../contexts/VendorContext';

/** Self-service password change, shown on the Profile page. */
const ChangePasswordCard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useVendor();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const inputClass =
    'block w-full rounded-md border border-gray-300 px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      const r = await apiFetch('/api/user/change-password/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Could not change the password.');
      // Tokens issued before the change keep working until they expire; sign
      // out so the user re-authenticates with the new password.
      logout();
      navigate('/login', { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg mt-4">
      <div className="px-4 sm:px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Password</h2>
          <p className="text-xs text-gray-500">Change the password you use to sign in.</p>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 min-h-[40px]">
          {open ? 'Cancel' : 'Change password'}
        </button>
      </div>
      {open && (
        <form onSubmit={submit} className="px-4 sm:px-6 pb-4 space-y-3">
          {error && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input type="password" required className={inputClass} placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
            <input type="password" required minLength={8} className={inputClass} placeholder="New password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
            <input type="password" required minLength={8} className={inputClass} placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          </div>
          <p className="text-xs text-gray-500">At least 8 characters, not too common. You will be signed out and asked to sign in again.</p>
          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium min-h-[40px] disabled:opacity-50">
              {saving ? 'Saving...' : 'Update password'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ChangePasswordCard;
